import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoaderKeys } from 'src/app/core/constants/constants';
import { AddBusinessOnboardingDetailsRequest, AddBusinessOnboardingDetailsResponse, AddBusinessOnboardingDetailsTempResponse, GetBusinessOnboardingDetailsRequest, GetBusinessOnboardingDetailsResponse, GetBusinessSegmentRequest, GetBusinessSegmentResponse, GetScrapePreviewProductsRequest, GetScrapePreviewProductsResponse, GetScrapeRemainingBusinessDetailsRequest, GetScrapeRemainingBusinessDetailsResponse, GetScrapedProductsProgressRequest, GetScrapedProductsProgressResponse, ScrapeBusinessOnboardingDetailsRequest, StartOnboardingCreativesRequest, StartOnboardingCreativesResponse, GetOnboardingCreativesRequest, GetOnboardingCreativesResponse, SaveOnboardingHeardAboutRequest, SaveOnboardingHeardAboutResponse, SaveOnboardingAdPainsRequest, SaveOnboardingAdPainsResponse,ScrapeBusinessOnboardingDetailsResponse, SkipScrapingRequest, UpdateBusinessOnboardingCompletionStatusRequest, UpdateBusinessOnboardingCompletionStatusResponse, UpdateBusinessOnboardingDetailsRequest, UpdateBusinessOnboardingDetailsResponse } from 'src/app/core/grpc-models/BusinessOnboarding_pb';
import { BusinessOnboardingProtoService } from 'src/app/core/grpc-services/BusinessOnboarding_pb_service';
import { GrpcService } from 'src/app/core/shared-services/grpc.service';
import { TokenService } from 'src/app/core/shared-services/token.service';

/** Navigation state set by the website screen's Skip: onboarding shows its form at once, with no store to read. */
export const ONBOARD_SKIPPED_WEBSITE_STATE = 'skippedWebsite';

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  isOnboardingBusinessDetailStepCompleted = false;
  isBusinessIntegrationTab = false;
  // isOnboardingBusinessIntegrationStepCompleted = false;
  isOnboardingCompleted?: boolean;
  businessCategoryName?: string;
  businessUsp?: string;

  businessOnboardingDetails = new GetBusinessOnboardingDetailsResponse().toObject();

  businessOnboardingDetailsSubject$ = new Subject<GetBusinessOnboardingDetailsResponse.AsObject>();
  businessOnboardingDetails$ = this.businessOnboardingDetailsSubject$.asObservable();

  private pendingOnboardingDetails: Promise<GetBusinessOnboardingDetailsResponse.AsObject> | null = null;
  private static readonly ONBOARDING_DETAILS_CACHE_MS = 3000;

  // A website scrape started on /web-scrapper. That screen goes to /onboard straight away, and
  // /onboard keeps its loading state up until this settles. Never rejects.
  private pendingScrape: Promise<void> | null = null;

  constructor(
    private readonly grpcService: GrpcService,
    private readonly tokenService: TokenService
  ) { }

  reset(): void {
    this.businessCategoryName = undefined;
    this.businessUsp = undefined;
  }

  setPendingScrape(scrape: Promise<void>): void {
    this.pendingScrape = scrape.catch(() => { });
  }

  /** Hands the in-flight scrape to its one reader (onboarding) and forgets it. */
  takePendingScrape(): Promise<void> | null {
    const scrape = this.pendingScrape;
    this.pendingScrape = null;
    return scrape;
  }

  public getBusinessOnboardingDetails(forceRefresh = false): Promise<GetBusinessOnboardingDetailsResponse.AsObject> {
    if (!forceRefresh && this.pendingOnboardingDetails) {
      return this.pendingOnboardingDetails;
    }

    const request = this.grpcService.grpcUnaryService(
      new GetBusinessOnboardingDetailsRequest(),
      BusinessOnboardingProtoService.GetBusinessOnboardingDetails,
      this.tokenService.getMetaData(), false
    ).then((response: GetBusinessOnboardingDetailsResponse.AsObject) => {
      // grpcUnaryService swallows errors into `undefined` — don't let a failed call
      // poison the cache for the next 3s of legitimate callers.
      if (!response) {
        this.pendingOnboardingDetails = null;
      }
      return response;
    });

    this.pendingOnboardingDetails = request;
    void request.finally(() => {
      setTimeout(() => {
        if (this.pendingOnboardingDetails === request) {
          this.pendingOnboardingDetails = null;
        }
      }, OnboardingService.ONBOARDING_DETAILS_CACHE_MS);
    });

    return request;
  }

  public getBusinessSegment(): Promise<GetBusinessSegmentResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      new GetBusinessSegmentRequest(),
      BusinessOnboardingProtoService.GetBusinessSegment,
      this.tokenService.getMetaData(), false
    );
  }

  public addBusinessOnboardingDetails(addOnboardingBusinessDetailsRequest: AddBusinessOnboardingDetailsRequest): Promise<AddBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      addOnboardingBusinessDetailsRequest,
      BusinessOnboardingProtoService.AddBusinessOnboardingDetails,
      this.tokenService.getMetaData(), true, LoaderKeys.AddOnboardingBusinessDetailsKey
    );
  }

  public addBusinessOnboardingDetailsTemp(addOnboardingBusinessDetailsRequest: AddBusinessOnboardingDetailsRequest): Promise<AddBusinessOnboardingDetailsTempResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      addOnboardingBusinessDetailsRequest,
      BusinessOnboardingProtoService.AddBusinessOnboardingDetailsTemp,
      undefined, true, LoaderKeys.AddOnboardingBusinessDetailsKey
    );
  }

  public updateBusinessOnboardingDetails(updateOnboardingBusinessDetailsRequest: UpdateBusinessOnboardingDetailsRequest): Promise<UpdateBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      updateOnboardingBusinessDetailsRequest,
      BusinessOnboardingProtoService.UpdateBusinessOnboardingDetails,
      this.tokenService.getMetaData(), true, LoaderKeys.UpdateOnboardingBusinessDetailsKey
    );
  }

  public updateOnboardingCompletionStatus(updateOnboardingCompletionStatusRequest: UpdateBusinessOnboardingCompletionStatusRequest): Promise<UpdateBusinessOnboardingCompletionStatusResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      updateOnboardingCompletionStatusRequest,
      BusinessOnboardingProtoService.UpdateBusinessOnboardingCompletionStatus,
      this.tokenService.getMetaData(), false, LoaderKeys.UpdateOnboardingCompletionStatusKey
    );
  }

  public scrapeBusinessOnboardingDetails(request: ScrapeBusinessOnboardingDetailsRequest): Promise<ScrapeBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.ScrapeBusinessOnboardingDetails,
      this.tokenService.getMetaData(), false, LoaderKeys.ScrapeOnboardingBusinessDetailsKey
    );
  }

  public skipScraping(): Promise<ScrapeBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      new SkipScrapingRequest(),
      BusinessOnboardingProtoService.SkipScraping,
      this.tokenService.getMetaData(), false
    );
  }


  public scrapeAndUpdateBusinessOnboardingDetails(request: UpdateBusinessOnboardingDetailsRequest): Promise<UpdateBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.ScrapeAndUpdateBusinessOnboardingDetails,
      this.tokenService.getMetaData(), false, LoaderKeys.ScrapeOnboardingBusinessDetailsKey
    );
  }

  public scrapeBusinessOnboardingDetailsTemp(request: ScrapeBusinessOnboardingDetailsRequest): Promise<ScrapeBusinessOnboardingDetailsResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.ScrapeBusinessOnboardingDetailsTemp,
      undefined, false, LoaderKeys.ScrapeOnboardingBusinessDetailsKey
    );
  }

  // The first products of the scraped website, asked for after the business details (works for guests too)
  public getScrapePreviewProducts(websiteUrl: string): Promise<GetScrapePreviewProductsResponse.AsObject> {
    const request = new GetScrapePreviewProductsRequest();
    request.setWebsiteurl(websiteUrl);
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.GetScrapePreviewProducts,
      undefined, false
    );
  }

  // The rest of the scraped business details (services, location, phone, ad goal, offer), asked for after the
  // screen's fields; detailsjson is the scraper's JSON object, empty when it has none (works for guests too)
  public getScrapeRemainingBusinessDetails(websiteUrl: string): Promise<GetScrapeRemainingBusinessDetailsResponse.AsObject> {
    const request = new GetScrapeRemainingBusinessDetailsRequest();
    request.setWebsiteurl(websiteUrl);
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.GetScrapeRemainingBusinessDetails,
      undefined, false
    );
  }

  // How many of the business's products the scrape has saved so far, and whether it is still saving them
  public getScrapedProductsProgress(): Promise<GetScrapedProductsProgressResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      new GetScrapedProductsProgressRequest(),
      BusinessOnboardingProtoService.GetScrapedProductsProgress,
      this.tokenService.getMetaData(), false
    );
  }

  // Starts the first creatives for the product picked on onboarding (by its page URL; empty: picked
  // automatically). Answers at once; they are made in the background.
  public startOnboardingCreatives(productUrl: string): Promise<StartOnboardingCreativesResponse.AsObject> {
    const request = new StartOnboardingCreativesRequest();
    request.setProducturl(productUrl);
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.StartOnboardingCreatives,
      this.tokenService.getMetaData(), false
    );
  }

  // The first creatives as they stand, for the "Made for you" popup; isstarted is false when none are known
  public getOnboardingCreatives(): Promise<GetOnboardingCreativesResponse.AsObject> {
    return this.grpcService.grpcUnaryService(
      new GetOnboardingCreativesRequest(),
      BusinessOnboardingProtoService.GetOnboardingCreatives,
      this.tokenService.getMetaData(), false
    );
  }

  /**
   * The answer to "How did you hear about Xoopah?", or a SKIP - pass an empty string for that.
   * Both end the question for good: what the server records is that it was put and dealt with,
   * not merely what was said, so a skip counts as firmly as an answer. A later answer overwrites
   * an earlier one (Back, then another channel), but a skip never blanks one.
   */
  public saveOnboardingHeardAbout(heardAbout: string): Promise<SaveOnboardingHeardAboutResponse.AsObject> {
    const request = new SaveOnboardingHeardAboutRequest();
    request.setHeardabout(heardAbout);
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.SaveOnboardingHeardAbout,
      this.tokenService.getMetaData(), false
    );
  }

  /**
   * The answers to "What's hardest about making ads?", comma separated. Writes once; an empty
   * string records nothing.
   */
  public saveOnboardingAdPains(adPains: string): Promise<SaveOnboardingAdPainsResponse.AsObject> {
    const request = new SaveOnboardingAdPainsRequest();
    request.setAdpains(adPains);
    return this.grpcService.grpcUnaryService(
      request,
      BusinessOnboardingProtoService.SaveOnboardingAdPains,
      this.tokenService.getMetaData(), false
    );
  }

}

